<template>
  <div ref="rootEl" :class="[$style.wrapper, slotSize > 1 ? $style.multiple : '', isEvent ? $style.event : '']">
    <Field v-slot="{ errors }" v-model="pictures" :name="name" :rules="rules">
      <VueDraggable v-model="pictures" draggable="[data-draggable]" :filter="filter" :prevent-on-filter="false" :delay="isSp ? 50 : 0" :class="[$style.photo, pictures.length > 0 ? $style.hasPictures : '']">
        <template v-if="pictures.length > 0">
          <div v-for="(picture, index) in pictures" :key="picture.filename" :class="$style.uploadedPhoto" data-draggable>
            <div :class="$style.uploadedPhoto">
              <div :class="[$style.uploadedPhotoContent, !imgLoaded(index) ? $style.loading : '']">
                <img :src="picture.url" alt="" @load="onLoadImg(index)" />
              </div>
              <div :class="$style.deletePicture" data-delete>
                <i class="icon-cross1" @click="onClickRemovePictureButton(index)"></i>
              </div>
              <div v-if="hasComment" :class="$style.comment" data-comment>
                <p :class="$style.commentEdit" @click="picture.isShowCommentEdit = true">
                  <span v-if="picture.text">{{ picture.text }}</span>
                  <span v-else :class="$style.commentEditDefault">コメントを追加</span>
                </p>
                <IModal v-model="picture.isShowCommentEdit" :with-close-button="false" @open="onOpenDialog" @close="onCloseDialog">
                  <ImageComment
                    :url="picture.url"
                    :text="picture.text"
                    @cancel="picture.isShowCommentEdit = false"
                    @save="
                      (comment) => {
                        picture.isShowCommentEdit = false;
                        picture.text = comment;
                      }
                    "
                  />
                </IModal>
              </div>
              <div v-if="slotSize > 1" :class="$style.uploadedPhotoIndex">{{ index + 1 }}</div>
            </div>
          </div>
        </template>
        <div v-if="availablePictureSlots > 0" :class="$style.formElement">
          <FileDroppable @file-dropped="onFileDropped">
            <div :class="[$style.upload, uploading ? $style.loading : '']" @click="onClickFileInput">
              <div :class="$style.content">
                <div :class="$style.icon"><SvgIcon :svg="ImageSvg" width="42" /></div>
                <p :class="$style.text">画像を追加</p>
              </div>
              <input ref="inputFileEl" type="file" accept="image/jpeg,image/png,image/gif" :multiple="slotSize > 1" @change="onChangePictures" />
            </div>
          </FileDroppable>
        </div>
      </VueDraggable>
      <p v-if="errors[0]" :class="$style.error">{{ errors[0] }}</p>
    </Field>
  </div>
</template>

<script setup lang="ts">
import { Field, useField } from 'vee-validate';
import { ref, computed } from 'vue';
import { VueDraggable } from 'vue-draggable-plus';
import { type Picture } from '@/types/photo-uploader';
import { useMQ } from '@/js/lib/helper/media-query';
import { uploadImage } from '@/js/lib/api/v1/upload';
import FileDroppable from '@/js/components/common/ui/FileDroppable.vue';
import IModal from '@/js/components/molecules/IModal.vue';
import SvgIcon from '@/js/components/molecules/SvgIcon.vue';
import ImageSvg from '@/svgs/vue/image.svg';
import ImageComment from './ImageComment.vue';

const props = withDefaults(
  defineProps<{
    name: string;
    rules?: string;
    hasComment?: boolean;
    slotSize?: number;
    isEvent?: boolean;
  }>(),
  {
    rules: '',
    slotSize: 1,
  },
);

const emit = defineEmits<{
  (e: 'loaded'): void;
}>();

let modalOpenPosition = 0;
const { isSp } = useMQ();
const { setErrors } = useField(props.name);
const uploading = ref(false);
const rootEl = ref<HTMLDivElement>();
const inputFileEl = ref<HTMLInputElement>();
const pictures = defineModel<Picture[]>({ required: true });
const filter = computed(() => [props.hasComment ? '[data-comment]' : undefined, '[data-delete]'].filter(Boolean).join(','));
const availablePictureSlots = computed(() => Math.max(props.slotSize - (pictures.value?.length ?? 0), 0));
const imgLoaded = computed(() => (index: number) => pictures.value[index]?.loaded);
const uploadPictures = async (files: File[]) => {
  if (files.length === 0) {
    return;
  }
  if (files.some((file) => file.size > 104857600)) {
    setErrors('画像は10MB以内にしてください');
    return;
  }

  uploading.value = true;

  try {
    const fileDatas = await Promise.all(
      files.map(async (file) => {
        const fileData = await uploadImage(file);
        return fileData;
      }),
    );
    pictures.value = [
      ...pictures.value,
      ...(fileDatas as Picture[]).map((fileData) => {
        return {
          ...fileData,
          isShowCommentEdit: false,
          loaded: false,
        };
      }),
    ];
  } catch {
    setErrors('画像をアップロードできませんでした');
  } finally {
    uploading.value = false;
  }
};
const onLoadImg = (index: number) => {
  pictures.value = pictures.value.map((picture, i) => (i === index ? { ...picture, loaded: true } : picture));
  emit('loaded');
};
const onClickRemovePictureButton = (index: number) => {
  pictures.value = pictures.value.filter((_picture, i) => i !== index);
};
const onOpenDialog = () => {
  modalOpenPosition = window.scrollY;
};
const onCloseDialog = () => {
  window.scrollTo(0, modalOpenPosition);
};
const onFileDropped = (files: File[]) => {
  uploadPictures(files.slice(0, availablePictureSlots.value));
};
const onClickFileInput = () => {
  if (inputFileEl.value) {
    inputFileEl.value.click();
  }
};
const onChangePictures = () => {
  if (inputFileEl.value?.files) {
    const files: File[] = [...inputFileEl.value.files];
    uploadPictures(files.slice(0, availablePictureSlots.value));
  }
};
</script>

<style module lang="scss">
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.wrapper {
  width: 240px;

  @media (--tablet) {
    width: auto;
  }

  &.multiple {
    width: auto;

    .photo {
      + .formElement {
        margin-top: 16px;
      }

      &.hasPictures {
        display: grid;
        gap: 12px;
        grid-template-columns: repeat(3, minmax(80px, 1fr));
        width: auto;

        @media (--tablet) {
          grid-template-columns: repeat(2, minmax(80px, 1fr));
        }

        :global(#container) & {
          grid-template-columns: repeat(2, minmax(80px, 1fr));
        }
      }
    }
  }
}

.loading {
  position: relative;

  &::before {
    background-color: var(--color-gray100);
    content: '';
    display: block;
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
  }

  &::after {
    animation: spin 0.5s linear infinite;
    border-color: rgb(100 100 100 / 90%) rgb(100 100 100 / 90%) rgb(127 127 127 / 30%) rgb(127 127 127 / 30%);
    border-radius: 50%;
    border-style: solid;
    border-width: 2px;
    content: '';
    display: block;
    height: 34px;
    left: 50%;
    margin-left: -15px;
    margin-top: -15px;
    position: absolute;
    top: 50%;
    width: 34px;
  }
}

.uploadedPhoto {
  position: relative;

  .deletePicture {
    position: absolute;
    right: -6px;
    top: -6px;

    > i {
      align-items: center;
      background-color: var(--color-gray800);
      border-radius: 50%;
      color: var(--color-white);
      cursor: pointer;
      display: flex !important;
      font-size: 12px;
      height: 20px;
      justify-content: center;
      margin: 0 !important;
      width: 20px;

      &:hover {
        opacity: 0.8;
      }
    }
  }

  .uploadedPhotoContent {
    background-color: #f0f0f0;
    border: 1px solid #f0f0f0;
    position: relative;
    text-align: center;

    .multiple & {
      cursor: grab;
    }

    .multiple :global(.sortable-chosen) & {
      cursor: grabbing;
    }

    img {
      aspect-ratio: 1 / 1;
      height: 100%;
      object-fit: contain;
      pointer-events: none;
      width: 100%;

      &:hover {
        opacity: 0.8;
      }

      .event & {
        aspect-ratio: 580 / 377;
      }
    }
  }

  .comment {
    border: 1px solid #f0f0f0;
    border-width: 0 1px 1px;

    .commentEdit {
      cursor: pointer;
      overflow: hidden;
      padding: 5px 8px;
      text-overflow: ellipsis;
      white-space: nowrap;

      &:hover {
        background-color: var(--color-gray100);
      }

      span {
        color: var(--color-black);

        &.commentEditDefault {
          color: var(--color-gray600);
        }
      }
    }
  }

  .uploadedPhotoIndex {
    align-items: center;
    background-color: var(--color-gray800);
    border-radius: 4px;
    color: var(--color-white);
    display: flex;
    font-size: 11px;
    height: 20px;
    justify-content: center;
    left: 5px;
    position: absolute;
    top: 5px;
    width: 20px;
  }
}

.upload {
  background-color: var(--color-gray100);
  border: 2px dashed var(--border-color);
  border-radius: 4px;
  cursor: pointer;
  padding: 62px 0;

  @media (--tablet) {
    padding: 39px 0;
  }

  &:hover {
    opacity: 0.8;
  }

  :global(.drag-over) > & {
    border: 2px solid var(--color-gray400);
  }

  .content {
    text-align: center;

    .icon {
      color: #868686;
      display: flex;
      gap: 10px;
      justify-content: center;
      margin: 8px 0;

      &::before {
        content: '+';
        display: block;
        font-size: 33px;
        font-weight: bold;
        line-height: 1;
      }

      > svg {
        fill: #868686;
      }
    }

    .text {
      color: #868686;
      display: block;
      font-weight: bold;
    }
  }

  input[type='file'] {
    display: none;
  }

  .multiple .hasPictures & {
    align-items: center;
    aspect-ratio: 1 / 1;
    display: flex;
    justify-content: center;
    padding: 0;
  }

  .event .hasPictures & {
    aspect-ratio: 580 / 377;
  }
}

.error {
  color: var(--color-dark-red);
  font-size: 12px;
  padding-top: 5px;
  text-align: left;
}
</style>
